// apps/web/src/lib/api/types-errors.ts
//
// TypeScript mirror of api/DTOs/ApiErrorEnvelope.cs. Every API call that
// fails returns this shape inside the response body. Consume it via the
// <ErrorPanel> component.

export interface ApiErrorEnvelope {
  error: ApiError;
}

export interface ApiError {
  code:          string;
  title:         string;
  message:       string;
  suggestion:    string | null;
  correlationId: string;
  timestamp:     string;
  technical:     TechnicalDetails | null;
}

export interface TechnicalDetails {
  exceptionType: string | null;
  source:        string | null;
  file:          string | null;
  line:          number | null;
  stackFrames:   string[];
}

/**
 * Type guard — narrow an unknown thrown value to ApiErrorEnvelope.
 *
 * Use this in catch blocks of fetch wrappers to convert axios/fetch
 * errors into the structured envelope. If it's not an envelope (network
 * error, parse failure, etc.), build a synthetic one so the UI always
 * has something to render.
 */
export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!v.error || typeof v.error !== 'object') return false;
  const e = v.error as Record<string, unknown>;
  return typeof e.code === 'string'
      && typeof e.title === 'string'
      && typeof e.message === 'string'
      && typeof e.correlationId === 'string';
}

/**
 * Build a synthetic envelope for client-side errors (network drops,
 * unexpected exceptions in fetch wrappers, etc.) so the UI can always
 * render an <ErrorPanel> regardless of where the failure originated.
 */
export function buildClientErrorEnvelope(
  err: unknown,
  fallbackTitle = 'Request failed'
): ApiErrorEnvelope {
  const message =
    err instanceof Error ? err.message :
    typeof err === 'string' ? err :
    'An unknown error occurred while contacting the server.';

  return {
    error: {
      code:          'client.network_error',
      title:         fallbackTitle,
      message,
      suggestion:    'Check your connection and try again. If this keeps happening, the server may be down.',
      correlationId: `client_${Date.now().toString(36)}`,
      timestamp:     new Date().toISOString(),
      technical:     err instanceof Error ? {
        exceptionType: err.name,
        source:        null,
        file:          null,
        line:          null,
        stackFrames:   (err.stack ?? '').split('\n').slice(0, 6).map(s => s.trim()).filter(Boolean),
      } : null,
    }
  };
}
